import { joinCarrier, signupCarrier } from '../../data/CarrierAPI';
import { useMemo, useState } from 'react';
import { WizardDataType } from '../../core/Wizard';
import { captureError, logInfo, logWarning } from 'shared/utils/datadog';
import { isChromium } from 'shared/helpers/browserHelpers';
import { identifyAnalytics } from 'shared/helpers/AnalyticsHelpers';
import { trackEvent } from 'shared/helpers/AnalyticsHelpers';
import { FORM_ERROR } from 'final-form';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useInitialState } from 'shared/helpers/useInitialState';
import { parseSearchParams } from 'shared/helpers/URLHelpers';
import { SignupCarrierType } from 'data/DTOs';
import { useSearchQuery } from 'shared/utils/RoutingHelpers';
import { useCustomerSupport } from 'shared/modules/botpress/useCustomerSupport';
import { submitCarrierHubSpot } from 'carrier/hubspot/submitCarrierHubSpotForm';
import { useCarrierFeatureToggles } from 'shared/useCarrierFeatureToggles';

export function useSignupCarrier() {
  const [{ utm_medium, utm_source, utm_campaign }] = useSearchQuery();
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [isSubmitting, setSubmitting] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [carrierData, setCarrierData] = useState<any>();
  const [joinEmail, setJoinEmail] = useState();
  const [verifiedUSDOT, setVerifiedUSDOT] = useState('');
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const { openBotpress } = useCustomerSupport();
  const { features } = useCarrierFeatureToggles();

  const [createAccountTitle, setCreateAccountTitle] =
    useState('Create Account');
  const { search } = useLocation();
  const navigate = useNavigate();
  const initialSearchState = useInitialState(search);

  const initialValues = useMemo<SignupCarrierType>(() => {
    const searchParams = parseSearchParams(initialSearchState || '');
    return {
      usdot: '',
      first_name: searchParams.first_name || '',
      last_name: searchParams.last_name || '',
      email: searchParams.email || '',
      password: '',
      role: '',
      role_other: '',
      number_of_trucks: 0,
      phoneNumber: '',
      heard_about_us: '',
      companyType: 'carrier',
      subscriptions: false,
      broker_name: '',
      referralSource: '',
      is_interstate: false,
      exempt_states: false,
      combined_truck: false,
      country: 'US',
    };
  }, [initialSearchState]);

  const clearVerifiedEmail = () => {
    setVerifiedEmail('');
    setVerifiedUSDOT('');
    setIsRegistered(false);
    setCarrierData(undefined);
  };

  const handleSubmit = async (values: any, wizardData: WizardDataType) => {
    if (values.email !== verifiedEmail) {
      logWarning('Carrier tried to sign up with unverified email', {
        email: values.email,
        verifiedEmail,
      });
      toast.error('Please verify your email address to continue');
      navigate({ pathname: '/carrier/create-account', search });
      return;
    }

    if (!captchaToken) {
      logWarning('Carrier missing Captcha token', values);
      // eslint-disable-next-line no-alert
      alert('Failed to verify Captcha. Please try again.');
      return;
    }

    if (isSubmitting) {
      return;
    }

    try {
      setSubmitting(true);
      if (!isRegistered || values.country !== 'US') {
        const signupData = {
          ...(values.country === 'US' && { usdot: verifiedUSDOT }),
          country: values.country,
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          password: values.password,
          phone_number: values.phoneNumber,
          role:
            values.role === 'Other'
              ? values.role_other.toLowerCase().replace(/\s/g, '_')
              : values.role.toLowerCase().replace(/\s/g, '_'),
          number_of_trucks: values.number_of_trucks,
          interested_in_eld: values.interested_in_eld,
          heard_about_us:
            values.comment !== ''
              ? values.comment === 'Other'
                ? values.referralSource
                : values.comment
              : undefined,
          company_type: 'carrier',
          email_subscription: values.subscriptions,
          sms_subscription: values.subscriptions,
          broker_name: values.broker_name,
          company_name: values.company_name,
          browser: isChromium() ? 'chromium' : 'other',
          cf_turnstile_response: captchaToken,
        };

        const { error, url, meta } = await signupCarrier(signupData);
        setSubmitting(false);

        if (meta.code === 302) {
          await submitCarrierHubSpot(
            {
              ...values,
              usdot:
                values.country === 'US'
                  ? verifiedUSDOT || values.usdot
                  : values.usdot,
            },
            { stage: 'step3_signup' },
          );
          identifyAnalytics(values.email, {
            email: values.email,
            name: values.first_name,
            companyName: values.company_name,
          });

          trackEvent('Sign up submitted', {
            companyName: values.company_name,
            companyType: wizardData.companyType,
            utm_medium,
            utm_source,
            utm_campaign,
          });
          logInfo('Carrier Sign up submitted', {
            ...signupData,
            password: null,
            cf_turnstile_response: null,
          });

          window.location.href = `${url}&next=${
            features?.ctms_homepage ? '/tms/home' : '/tms/loads'
          }?utm_source=carrier_signup`;
        } else {
          logWarning('Carrier signup failed', {
            error,
            values: {
              ...signupData,
              password: null,
              cf_turnstile_response: null,
            },
          });

          const validationErrors =
            error.type === 'ValidationError' &&
            !!error.context &&
            formatAPIErrors(error.context);

          if (validationErrors) {
            if (validationErrors.email || validationErrors.phone_number) {
              history.go(-2);
            }

            return validationErrors;
          }

          return {
            [FORM_ERROR]:
              error.user_message ||
              error.dev_message ||
              error.type ||
              'Failed to submit',
          };
        }
      } else {
        const signupData = {
          ...(values.country === 'US' && { usdot: verifiedUSDOT }),
          country: values.country,
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          password: values.password,
          number_of_trucks: values.number_of_trucks,
          role:
            values.role === 'Other'
              ? values.role_other.toLowerCase()
              : values.role.toLowerCase().replace(/\s/g, '_'),
          phone_number: values.phoneNumber,
          heard_about_us:
            values.comment !== ''
              ? values.comment === 'Other'
                ? values.referralSource
                : values.comment
              : undefined,
          company_type:
            values.number_of_trucks === '1' ? 'owner_operator' : 'fleet',
          email_subscription: values.subscriptions,
          sms_subscription: values.subscriptions,
          broker_name: values.broker_name,
          browser: isChromium() ? 'chromium' : 'other',
          cf_turnstile_response: captchaToken,
        };

        const { error, user_message, meta } = await joinCarrier(
          signupData,
          carrierData?.guid,
        );
        setSubmitting(false);

        if (meta.code === 200) {
          setJoinEmail(
            user_message
              ? user_message.match(
                  new RegExp(
                    /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi,
                  ),
                )[0]
              : '',
          );

          await submitCarrierHubSpot(
            {
              ...values,
              usdot:
                values.country === 'US'
                  ? verifiedUSDOT || values.usdot
                  : values.usdot,
            },
            { stage: 'step3_signup' },
          );
          identifyAnalytics(values.email, {
            email: values.email,
            name: values.first_name,
            company_name: values.company_name,
          });
          trackEvent('Join request sent', {
            companyType:
              values.number_of_trucks === '1' ? 'owner_operator' : 'fleet',
            utm_medium,
            utm_source,
            utm_campaign,
          });
          logInfo('Carrier Join request sent', {
            ...signupData,
            password: null,
            cf_turnstile_response: null,
          });
        } else {
          logWarning('Carrier join failed', {
            error,
            values: {
              ...signupData,
              password: null,
              cf_turnstile_response: null,
            },
          });

          const validationErrors =
            error.type === 'ValidationError' &&
            !!error.context &&
            formatAPIErrors(error.context);

          if (validationErrors) {
            if (validationErrors.email || validationErrors.phone_number) {
              history.go(-2);
            }

            return validationErrors;
          }

          return {
            [FORM_ERROR]:
              error.user_message ||
              error.dev_message ||
              error.type ||
              'Failed to submit',
          };
        }
      }
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.log(e);
      setSubmitting(false);
      // eslint-disable-next-line no-alert
      alert('Something went wrong! Please contact support!');
      openBotpress();
      captureError(e, 'CarrierSignup#submit', {
        ...values,
        password: null,
      });
    }
  };

  return {
    verifiedUSDOT,
    setVerifiedUSDOT,
    verifiedEmail,
    setVerifiedEmail,
    clearVerifiedEmail,
    createAccountTitle,
    setCreateAccountTitle,
    handleSubmit,
    isSubmitting,
    initialValues,
    setCarrierData,
    setCaptchaToken,
    joinEmail,
    setIsRegistered,
    captchaToken,
    carrierData,
  };
}

function formatAPIErrors(
  context: Record<string, unknown>,
): Record<string, string> | undefined {
  const errors: Record<string, string> = {};

  for (const field in context) {
    const error = context[field];
    if (Array.isArray(error)) {
      errors[field] = error.join(', ');
    } else if (typeof error === 'string') {
      errors[field] = error;
    }
  }

  if (Object.keys(errors).length) {
    return errors;
  }

  return undefined;
}
